import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, XCircle, AlertCircle, CheckCircle2, Clock, CreditCard, HelpCircle } from 'lucide-react';
import TexHubLogo from '@/components/brand/TexHubLogo';
import { useLocale } from '@/contexts/LocaleContext';

const CancellationPage = () => {
  const { isUSA } = useLocale();
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <TexHubLogo size="small" />
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-4 max-w-4xl py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center">
            <XCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              {isUSA ? 'Cancellation and Refund Policy' : 'Política de Cancelación y Reembolsos'}
            </h1>
            <p className="text-slate-500">{isUSA ? 'Last updated: March 2025' : 'Última actualización: Marzo 2025'}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-sm space-y-8">
          
          {/* Quick Summary */}
          <section className="bg-blue-50 -mx-8 -mt-8 p-6 rounded-t-xl border-b border-blue-100">
            <h2 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {isUSA ? 'Quick Summary' : 'Resumen Rápido'}
            </h2>
            <ul className="space-y-2 text-blue-800">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>{isUSA ? 'You can cancel your subscription at any time' : 'Puedes cancelar tu suscripción en cualquier momento'}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>{isUSA ? 'You keep access until the end of your paid period' : 'Conservas el acceso hasta el final de tu período pagado'}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>{isUSA ? 'No cancellation fees or penalties' : 'No hay cargos por cancelación ni penalizaciones'}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>{isUSA ? 'Refunds available within the first 7 days' : 'Reembolsos disponibles dentro de los primeros 7 días'}</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-600" />
              {isUSA ? '1. Subscription Cancellation' : '1. Cancelación de Suscripciones'}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {isUSA 
                ? 'At TexHub we believe in flexibility. You can cancel your subscription at any time without questions or complications.'
                : 'En TexHub creemos en la flexibilidad. Puedes cancelar tu suscripción en cualquier momento sin preguntas ni complicaciones.'}
            </p>
            
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">{isUSA ? '1.1 How to cancel' : '1.1 Cómo cancelar'}</h3>
            <ol className="list-decimal list-inside text-slate-600 space-y-2 ml-2">
              <li>{isUSA ? 'Log in to your TexHub account' : 'Inicia sesión en tu cuenta de TexHub'}</li>
              <li>{isUSA ? 'Go to' : 'Ve a'} <strong>{isUSA ? 'Settings → Subscription' : 'Configuración → Suscripción'}</strong></li>
              <li>{isUSA ? 'Click' : 'Haz clic en'} <strong>"{isUSA ? 'Cancel subscription' : 'Cancelar suscripción'}"</strong></li>
              <li>{isUSA ? 'Confirm cancellation' : 'Confirma la cancelación'}</li>
            </ol>
            
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">{isUSA ? '1.2 What happens when you cancel' : '1.2 Qué sucede al cancelar'}</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-2">
              <li>{isUSA ? 'Your subscription will remain active until the end of the current billing period' : 'Tu suscripción permanecerá activa hasta el final del período de facturación actual'}</li>
              <li>{isUSA ? 'No more automatic charges will be made' : 'No se realizarán más cobros automáticos'}</li>
              <li>{isUSA ? 'You will keep access to all features of your plan until the expiration date' : 'Conservarás acceso a todas las funciones de tu plan hasta la fecha de vencimiento'}</li>
              <li>{isUSA ? 'Your account will switch to the free plan after the expiration date' : 'Tu cuenta pasará al plan gratuito después de la fecha de vencimiento'}</li>
              <li>{isUSA ? 'Your data and content will be preserved (not deleted)' : 'Tus datos y contenido se conservarán (no se eliminan)'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-slate-600" />
              {isUSA ? '2. Refund Policy' : '2. Política de Reembolsos'}
            </h2>
            
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">{isUSA ? '2.1 Guarantee period (7 days)' : '2.1 Período de garantía (7 días)'}</h3>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? <>If you are not satisfied with your subscription, you can request a full refund within the first <strong>7 days</strong> after your first payment. This guarantee period applies only to new subscribers.</>
                : <>Si no estás satisfecho con tu suscripción, puedes solicitar un reembolso completo dentro de los primeros <strong>7 días</strong> después de tu primer pago. Este período de garantía aplica solo para nuevos suscriptores.</>}
            </p>
            
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">{isUSA ? '2.2 Refunds after the guarantee period' : '2.2 Reembolsos después del período de garantía'}</h3>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'After the first 7 days, we generally do not offer refunds for the current billing period. However, we evaluate each case individually. You can request a review if:'
                : 'Después de los primeros 7 días, generalmente no ofrecemos reembolsos por el período de facturación actual. Sin embargo, evaluamos cada caso individualmente. Puedes solicitar una revisión si:'}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-2 mt-2">
              <li>{isUSA ? 'You experienced serious technical issues that prevented using the service' : 'Experimentaste problemas técnicos graves que impidieron el uso del servicio'}</li>
              <li>{isUSA ? 'A duplicate charge was made by mistake' : 'Se realizó un cobro duplicado por error'}</li>
              <li>{isUSA ? 'You could not cancel before automatic renewal due to extraordinary circumstances' : 'No pudiste cancelar antes de la renovación automática por circunstancias extraordinarias'}</li>
            </ul>
            
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">{isUSA ? '2.3 Non-refundable cases' : '2.3 Reembolsos NO aplicables'}</h3>
            <p className="text-slate-600 leading-relaxed">
              {isUSA ? 'Refunds will not be granted in the following cases:' : 'No se otorgarán reembolsos en los siguientes casos:'}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-2 mt-2">
              <li>{isUSA ? 'Cancellations made after the 7-day guarantee period' : 'Cancelaciones realizadas después del período de garantía de 7 días'}</li>
              <li>{isUSA ? 'Accounts suspended or cancelled for violation of Terms of Service' : 'Cuentas suspendidas o canceladas por violación de los Términos de Servicio'}</li>
              <li>{isUSA ? 'Downgrades to a lower cost plan (new price applies in next cycle)' : 'Cambios de plan a uno de menor costo (se aplica el nuevo precio en el siguiente ciclo)'}</li>
              <li>{isUSA ? 'AI image packs already partially or fully used' : 'Paquetes de imágenes IA ya utilizados parcial o totalmente'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">{isUSA ? '3. Free Plan' : '3. Plan Gratuito'}</h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'TexHub offers a free plan with the following features:'
                : 'TexHub ofrece un plan gratuito con las siguientes características:'}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-2 mt-3">
              <li>{isUSA ? 'Up to 5 products in your catalog' : 'Hasta 5 productos en tu catálogo'}</li>
              <li>{isUSA ? 'Basic company profile visible in the directory' : 'Perfil básico de empresa visible en el directorio'}</li>
              <li>{isUSA ? 'Receive up to 5 inquiries from potential clients' : 'Recibir hasta 5 consultas de clientes potenciales'}</li>
              <li>{isUSA ? 'No credit card required to start' : 'Sin tarjeta de crédito requerida para empezar'}</li>
              <li>{isUSA ? 'Upgrade to PRO anytime to unlock all features' : 'Mejora a PRO cuando quieras para desbloquear todas las funciones'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">{isUSA ? '4. AI Image Packs (TexVision)' : '4. Paquetes de Imágenes IA (TexVision)'}</h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'AI image packs are one-time purchases (not subscriptions) and have specific policies:'
                : 'Los paquetes de imágenes IA son compras únicas (no suscripciones) y tienen políticas específicas:'}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-2 mt-3">
              <li><strong>{isUSA ? 'Full refund' : 'Reembolso completo'}:</strong> {isUSA ? 'If you haven\'t used any image from the pack' : 'Si no has utilizado ninguna imagen del paquete'}</li>
              <li><strong>{isUSA ? 'Partial refund' : 'Reembolso parcial'}:</strong> {isUSA ? 'Not available once at least one image has been used' : 'No disponible una vez que se ha usado al menos una imagen'}</li>
              <li><strong>{isUSA ? 'Validity' : 'Vigencia'}:</strong> {isUSA ? 'Unused images do not expire' : 'Las imágenes no utilizadas no expiran'}</li>
              <li><strong>{isUSA ? 'Transfer' : 'Transferencia'}:</strong> {isUSA ? 'Packs are not transferable between accounts' : 'Los paquetes no son transferibles entre cuentas'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">{isUSA ? '5. Refund Process' : '5. Proceso de Reembolso'}</h2>
            
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">{isUSA ? '5.1 How to request a refund' : '5.1 Cómo solicitar un reembolso'}</h3>
            <ol className="list-decimal list-inside text-slate-600 space-y-2 ml-2">
              <li>{isUSA ? 'Send an email to' : 'Envía un correo a'} <strong>Info@texhub.com.mx</strong></li>
              <li>{isUSA ? 'Include your name, registered email and reason for the request' : 'Incluye tu nombre, correo registrado y motivo de la solicitud'}</li>
              <li>{isUSA ? 'You will receive a response within a maximum of 3 business days' : 'Recibirás una respuesta en un máximo de 3 días hábiles'}</li>
            </ol>
            
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">{isUSA ? '5.2 Processing time' : '5.2 Tiempo de procesamiento'}</h3>
            <p className="text-slate-600 leading-relaxed">
              {isUSA ? 'Once the refund is approved:' : 'Una vez aprobado el reembolso:'}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-2 mt-2">
              <li><strong>{isUSA ? 'Credit/debit cards' : 'Tarjetas de crédito/débito'}:</strong> {isUSA ? '5-10 business days' : '5-10 días hábiles'}</li>
              <li>{isUSA ? 'The refund will be made to the same payment method originally used' : 'El reembolso se realizará al mismo método de pago utilizado originalmente'}</li>
              <li>{isUSA ? 'You will receive a confirmation email when the refund is processed' : 'Recibirás un correo de confirmación cuando se procese el reembolso'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-slate-600" />
              {isUSA ? '6. Contact' : '6. Contacto'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'For any questions about cancellations or refunds:'
                : 'Para cualquier pregunta sobre cancelaciones o reembolsos:'}
            </p>
            <div className="bg-slate-50 p-4 rounded-lg mt-4">
              <p className="text-slate-700">
                <strong>Email:</strong> Info@texhub.com.mx<br />
                <strong>{isUSA ? 'Business hours' : 'Horario de atención'}:</strong> {isUSA ? 'Monday to Friday, 9:00 AM - 6:00 PM (Mexico City time)' : 'Lunes a Viernes, 9:00 - 18:00 (hora Ciudad de México)'}<br />
                <strong>{isUSA ? 'Response time' : 'Tiempo de respuesta'}:</strong> {isUSA ? 'Maximum 3 business days' : 'Máximo 3 días hábiles'}
              </p>
            </div>
          </section>

          <section className="bg-amber-50 -mx-8 -mb-8 p-6 rounded-b-xl mt-8 border-t border-amber-100">
            <p className="text-sm text-amber-800">
              <strong>{isUSA ? 'Note' : 'Nota'}:</strong> {isUSA 
                ? 'This policy may be updated occasionally. Changes will be posted on this page and, if significant, we will notify you by email. We recommend reviewing this policy periodically.'
                : 'Esta política puede ser actualizada ocasionalmente. Los cambios serán publicados en esta página y, si son significativos, te notificaremos por correo electrónico. Te recomendamos revisar esta política periódicamente.'}
            </p>
          </section>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/terms">
            <Button variant="outline">
              {isUSA ? 'Terms and Conditions' : 'Términos y Condiciones'}
            </Button>
          </Link>
          <Link to="/privacy">
            <Button variant="outline">
              {isUSA ? 'Privacy Policy' : 'Aviso de Privacidad'}
            </Button>
          </Link>
          <Link to="/">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {isUSA ? 'Back to home' : 'Volver al inicio'}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CancellationPage;
